import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, Mail, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

const AdminEmailTesting = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState<string | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  const sendTestEmail = async (emailType: string, testData: any) => {
    if (!email) {
      toast({
        title: "Email Required",
        description: "Please enter a recipient email address",
        variant: "destructive",
      });
      return;
    }

    setLoading(emailType);
    try {
      const { error } = await supabase.functions.invoke(emailType, {
        body: { ...testData, email },
      });

      if (error) throw error;

      toast({
        title: "Test Email Sent",
        description: `Check ${email} for the test email`,
      });
    } catch (error: any) {
      console.error("Error sending test email:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to send test email",
        variant: "destructive",
      });
    } finally {
      setLoading(null);
    }
  };

  const testPickupConfirmation = () => {
    const pickupDate = new Date();
    pickupDate.setDate(pickupDate.getDate() + 7);

    sendTestEmail("send-pickup-confirmation", {
      pickupDate: pickupDate.toISOString(),
      boxes: [
        { box_name: "Winter Clothes Box", box_number: "ES-001" },
        { box_name: "Kitchen Items Box", box_number: "ES-002" },
      ],
      totalCost: 19.98,
      boxCount: 2,
    });
  };

  const testShippingLabelsReady = () => {
    const pickupDate = new Date();
    pickupDate.setDate(pickupDate.getDate() + 7);

    sendTestEmail("send-shipping-labels-ready", {
      labels: [
        {
          boxName: "Winter Clothes Box",
          boxNumber: "ES-001",
          trackingNumber: "1Z999AA10123456784",
          labelUrl: "https://example.com/label-1.pdf",
        },
        {
          boxName: "Kitchen Items Box",
          boxNumber: "ES-002",
          trackingNumber: "1Z999AA10123456785",
          labelUrl: "https://example.com/label-2.pdf",
        },
      ],
      pickupDate: pickupDate.toISOString(),
    });
  };

  const testPaymentReceipt = () => {
    sendTestEmail("send-payment-receipt", {
      amount: 1998,
      currency: "usd",
      paymentDate: new Date().toISOString(),
      invoiceNumber: `INV-TEST-${Date.now()}`,
      description: "Monthly Storage Subscription - 2 Boxes",
      invoiceUrl: "https://example.com/invoice-test.pdf",
    });
  };

  const testPaymentFailed = () => {
    const nextRetry = new Date();
    nextRetry.setDate(nextRetry.getDate() + 3);

    sendTestEmail("send-payment-failed", {
      amount: 1998,
      currency: "usd",
      attemptDate: new Date().toISOString(),
      nextRetryDate: nextRetry.toISOString(),
      updatePaymentUrl: "https://example.com/update-payment",
    });
  };

  const testSignupWelcome = () => {
    sendTestEmail("send-signup-welcome", {
      name: "Test User",
    });
  };

  const emailTests = [
    {
      id: "send-pickup-confirmation",
      title: "Pickup Confirmation",
      description: "Sent after scheduling a pickup",
      testData: "2 boxes, pickup in 7 days, $19.98/month",
      handler: testPickupConfirmation,
    },
    {
      id: "send-shipping-labels-ready",
      title: "Shipping Labels Ready",
      description: "Sent when shipping labels are generated",
      testData: "2 labels with tracking numbers and PDFs",
      handler: testShippingLabelsReady,
    },
    {
      id: "send-payment-receipt",
      title: "Payment Receipt",
      description: "Sent after successful payment",
      testData: "$19.98 payment, monthly subscription",
      handler: testPaymentReceipt,
    },
    {
      id: "send-payment-failed",
      title: "Payment Failed",
      description: "Sent when payment fails",
      testData: "$19.98 failed, retry in 3 days",
      handler: testPaymentFailed,
    },
    {
      id: "send-signup-welcome",
      title: "Welcome Email",
      description: "Sent after user signup",
      testData: "Welcome message with getting started steps",
      handler: testSignupWelcome,
    },
  ];

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-4xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate("/admin")}
          className="mb-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Admin
        </Button>

        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Email Testing</h1>
          <p className="text-muted-foreground">
            Test transactional emails with realistic data
          </p>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Recipient Email</CardTitle>
            <CardDescription>
              Enter the email address where test emails should be sent
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <div className="flex-1">
                <Label htmlFor="email" className="sr-only">
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your.email@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-4">
          {emailTests.map((test) => (
            <Card key={test.id}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  {test.title}
                </CardTitle>
                <CardDescription>{test.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    <strong>Test Data:</strong> {test.testData}
                  </div>
                  <Button
                    onClick={test.handler}
                    disabled={loading === test.id || !email}
                  >
                    {loading === test.id ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      "Send Test Email"
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="mt-6 border-yellow-500/50 bg-yellow-500/5">
          <CardHeader>
            <CardTitle className="text-yellow-700 dark:text-yellow-500">
              Testing Notes
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground space-y-2">
            <p>
              • All test emails use pre-filled realistic data marked as "TEST"
            </p>
            <p>
              • Links in test emails may point to placeholder URLs
            </p>
            <p>
              • Check your spam folder if emails don't appear in inbox
            </p>
            <p>
              • Test emails use the same Resend configuration as production
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminEmailTesting;
